import { createIyzicoCheckoutSessionFn } from "@/actions/payment/handler";
import { listProgramsFn, listProgramsOptions } from "@/actions/program/handler";
import { assignProgramFn } from "@/actions/user-program/handler";
import { getActiveUserProgramOptions } from "@/actions/user-program/handler";
import { authClient } from "@/auth/auth-client";
import { IyzicoCheckoutDialog } from "@/components/payment/IyzicoCheckoutDialog";
import { JoinWithCodeDialog } from "@/components/program/JoinWithCodeDialog";
import { ProgramActivationWizard } from "@/components/program/ProgramActivationWizard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { seo } from "@/utils/seo";
import * as Sentry from "@sentry/tanstackstart-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Calendar,
  Clock,
  Dumbbell,
  KeyRound,
  Lock,
  ShoppingBag,
  Unlock,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";

const programSearchSchema = z.object({
  payment: z
    .enum(["success", "failed"])
    .catch(undefined as any)
    .optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/_app/program/")({
  validateSearch: (search) => programSearchSchema.parse(search),
  head: () => ({
    meta: seo({
      title: "Antrenman Programları | AtalayTheCoach",
      description:
        "Hedeflerinize yönelik profesyonel powerlifting ve güç antrenman programları. Programını seç ve gelişimini takip et.",
      canonical: "https://atalaythecoach.com/program",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Antrenman Programları | AtalayTheCoach",
          description: "Profesyonel antrenman programları listesi.",
          url: "https://atalaythecoach.com/program",
        }),
      },
    ],
  }),
  component: ProgramPage,
});

function ProgramPage() {
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (search.payment === "success") {
      toast.success("Ödeme Başarılı!", {
        description: "Programınız hesabınıza tanımlandı. İyi antrenmanlar! 🎉",
      });
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    } else if (search.payment === "failed") {
      toast.error("Ödeme Başarısız", {
        description: search.error || "Ödeme işlemi tamamlanamadı.",
      });
    }
  }, [search.payment, search.error, queryClient]);

  return (
    <div className="container mx-auto max-w-4xl px-4 pt-24 pb-8">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-bold text-3xl text-foreground">
            Antrenman Programları
          </h1>
          <p className="mt-2 text-muted-foreground">
            Programını seç ve antrenmanlarını takip et
          </p>
        </div>
      </div>

      <ProgramList />
    </div>
  );
}

function ProgramList() {
  const {
    data: programsData,
    isLoading: isProgramsLoading,
    error: programsError,
  } = useQuery({
    ...listProgramsOptions(),
    queryFn: async () => {
      try {
        return await listProgramsFn();
      } catch (e) {
        Sentry.withScope((scope) => {
          scope.setTag("page", "/program");
          scope.setTag("query", "listPrograms");
          scope.setExtra("error", e);
          Sentry.captureException(
            e instanceof Error ? e : new Error(String(e)),
          );
        });
        throw e;
      }
    },
  });
  const { data: activeProgramData } = useQuery(getActiveUserProgramOptions());

  const [code, setCode] = React.useState("");
  const navigate = useNavigate();

  const programs = programsData?.body?.programs ?? [];
  const activeProgram =
    activeProgramData?.body && "programId" in activeProgramData.body
      ? activeProgramData.body
      : null;

  if (isProgramsLoading) {
    return <ProgramListSkeleton />;
  }

  if (programsError) {
    Sentry.withScope((scope) => {
      scope.setTag("page", "/program");
      scope.setTag("error_type", "programs_load_failed");
      scope.setExtra("programsError", programsError);
      Sentry.captureMessage(
        "Program listesi yuklenemedi - muhtemel auth/csrf hatasi",
        "warning",
      );
    });
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="mb-4 h-12 w-12 text-destructive" />
          <h3 className="mb-2 font-semibold text-lg">Programlar Yüklenemedi</h3>
          <p className="text-center text-muted-foreground">
            Programlar yüklenirken bir hata oluştu.
            <br />
            Lütfen sayfayı yenileyin veya tekrar giriş yapın.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Sayfayı Yenile
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (programs.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Dumbbell className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 font-semibold text-lg">Henüz Program Yok</h3>
          <p className="text-center text-muted-foreground">
            Aktif antrenman programı bulunmuyor.
            <br />
            Yakında yeni programlar eklenecek!
          </p>
        </CardContent>
      </Card>
    );
  }

  const activeId = activeProgram?.programId;

  // Sort active program to top within each list
  const sortActive = (list: typeof programs) =>
    [...list].sort((a, b) => {
      if (a.programId === activeId) return -1;
      if (b.programId === activeId) return 1;
      return 0;
    });

  // Free: not marked as paid, no price, no accessCode
  const freePrograms = sortActive(
    programs.filter((p) => !p.isPaid && !p.price && !p.accessCode),
  );

  // Paid: isPaid || has price || has accessCode
  const paidPrograms = sortActive(
    programs.filter((p) => p.isPaid || !!p.price || !!p.accessCode),
  );

  const handleJoinWithCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    navigate({
      to: "/join/$code",
      params: { code: code.trim().toUpperCase() },
    });
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="free" className="w-full">
        <div className="-mx-4 sticky top-0 z-30 mb-6 bg-background/95 px-4 py-4 backdrop-blur-sm md:mx-0 md:px-0">
          <TabsList className="flex h-auto w-full flex-wrap justify-center gap-2 border-none bg-transparent p-0 shadow-none">
            <TabsTrigger
              value="free"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              <span className="hidden sm:inline">Ücretsiz Programlar</span>
              <span className="sm:hidden">Ücretsiz</span>
            </TabsTrigger>
            <TabsTrigger
              value="paid"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              <span className="hidden sm:inline">Ücretli Programlar</span>
              <span className="sm:hidden">Ücretli</span>
            </TabsTrigger>
            <TabsTrigger
              value="code"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              Kodla Katıl
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="free" className="mt-2 outline-none">
          {freePrograms.length === 0 ? (
            <Card className="border-border/40 bg-card/30">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Unlock className="mb-4 h-10 w-10 text-muted-foreground/40" />
                <h3 className="mb-1 font-bold text-lg">
                  Hazır Ücretsiz Program Bulunmuyor
                </h3>
                <p className="text-muted-foreground text-sm">
                  Şu anda listelenecek ücretsiz hazır program bulunmamaktadır.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {freePrograms.map((program) => (
                <ProgramCard
                  key={program.programId}
                  program={program}
                  isActive={program.programId === activeId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="paid" className="mt-2 outline-none">
          {paidPrograms.length === 0 ? (
            <Card className="border-border/40 bg-card/30">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Lock className="mb-4 h-10 w-10 text-muted-foreground/40" />
                <h3 className="mb-1 font-bold text-lg">
                  Hazır Ücretli Program Bulunmuyor
                </h3>
                <p className="text-muted-foreground text-sm">
                  Şu anda listelenecek ücretli hazır program bulunmamaktadır.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {paidPrograms.map((program) => (
                <ProgramCard
                  key={program.programId}
                  program={program}
                  isActive={program.programId === activeId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="code" className="mt-2 outline-none">
          <Card className="relative mx-auto max-w-md overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-b from-card to-accent/20 shadow-primary/5 shadow-xl">
            {/* Subtle glow elements */}
            <div className="-right-12 -top-12 pointer-events-none absolute h-32 w-32 rounded-full bg-primary/5 blur-3xl" />
            <div className="-left-12 -bottom-12 pointer-events-none absolute h-32 w-32 rounded-full bg-primary/5 blur-3xl" />

            <CardContent className="relative z-10 p-8">
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-inner transition-all duration-300 hover:rotate-6 hover:scale-110">
                  <KeyRound className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-foreground text-xl tracking-tight">
                    Özel Programa Katıl
                  </h3>
                  <p className="text-muted-foreground/80 text-sm leading-relaxed">
                    Koçunuz veya katıldığınız grup tarafından size iletilen 6
                    haneli özel erişim kodunu girerek programınıza anında
                    başlayabilirsiniz.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleJoinWithCodeSubmit}
                className="mt-8 space-y-6"
              >
                <div className="space-y-3">
                  <label
                    htmlFor="tab-access-code"
                    className="ml-1 block font-bold text-muted-foreground text-xs uppercase tracking-wider"
                  >
                    Erişim Kodu
                  </label>
                  <input
                    id="tab-access-code"
                    placeholder="ÖRN: JSUAYX"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={cn(
                      "flex h-14 w-full rounded-2xl border-2 border-muted bg-background/50 text-center font-bold font-mono text-2xl uppercase tracking-[0.5em] shadow-sm transition-all placeholder:text-muted-foreground/30 placeholder:tracking-normal focus:bg-background focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/10",
                      code.length === 6 && "border-primary/50 bg-background",
                    )}
                    maxLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl bg-primary font-bold text-primary-foreground shadow-md shadow-primary/10 transition-all hover:bg-primary/95 hover:shadow-lg hover:shadow-primary/15 active:scale-[0.98]"
                  disabled={code.length < 6}
                >
                  Programa Git ve Başla
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface ProgramCardProps {
  program: {
    programId: string;
    name: string;
    description?: string | null;
    durationWeeks: number;
    daysPerWeek: number;
    difficulty: number;
    image?: string | null;
    accessCode?: string | null;
    isPaid?: boolean | null;
    price?: number | null;
    oldPrice?: number | null;
  };
  isActive?: boolean;
}

function ProgramCard({ program, isActive }: ProgramCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();

  const isPaidProgram = Boolean(
    program.isPaid || program.price || program.accessCode,
  );

  const [checkoutUrl, setCheckoutUrl] = React.useState<string | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = React.useState(false);

  const assignMutation = useMutation({
    mutationFn: assignProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
      navigate({
        to: "/program/$programId",
        params: { programId: program.programId },
      });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: createIyzicoCheckoutSessionFn,
    onSuccess: (res) => {
      if (
        res.status === 200 &&
        res.body &&
        "paymentPageUrl" in res.body &&
        res.body.paymentPageUrl
      ) {
        setCheckoutUrl(res.body.paymentPageUrl);
        setIsCheckoutOpen(true);
      } else if (res.body && "error" in res.body) {
        toast.error(res.body.error);
      }
    },
    onError: (err) => {
      toast.error((err as Error).message || "Ödeme başlatılamadı");
    },
  });

  const handlePurchase = () => {
    if (!session?.user) {
      toast.info("Programı satın almak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/program" },
      });
      return;
    }
    checkoutMutation.mutate({
      data: {
        programId: program.programId,
      },
    });
  };

  const handleAssign = () => {
    if (!session?.user) {
      toast.info("Programı başlatmak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/program" },
      });
      return;
    }
    assignMutation.mutate({
      data: {
        programId: program.programId,
      },
    });
  };

  const discountPercent =
    program.price && program.oldPrice && program.oldPrice > program.price
      ? Math.round(
          ((program.oldPrice - program.price) / program.oldPrice) * 100,
        )
      : null;

  return (
    <div
      className={cn(
        "group flex flex-col overflow-hidden transition-all duration-300",
        isActive && "rounded-3xl bg-secondary/30",
        !isActive && "rounded-2xl border border-transparent hover:bg-muted/30",
      )}
    >
      <div className="flex h-full flex-col">
        {program.image && (
          <div className="aspect-video w-full overflow-hidden rounded-xl border border-border/40">
            <img
              src={program.image}
              alt={program.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col p-6">
          <div className="mb-6 flex-1 space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-bold text-2xl tracking-tight">
                  {program.name}
                </h3>
                {isPaidProgram && (
                  <Badge
                    variant="outline"
                    className="border-primary/20 bg-primary/5 font-bold text-[10px] text-primary uppercase"
                  >
                    <Lock className="mr-1 h-3 w-3" /> Ücretli
                  </Badge>
                )}
                {discountPercent !== null && (
                  <Badge className="border-emerald-500/20 bg-emerald-500/10 font-bold text-[10px] text-emerald-600 dark:text-emerald-400">
                    %{discountPercent} İndirim
                  </Badge>
                )}
              </div>
              {isActive && (
                <p className="font-bold text-primary text-xs uppercase tracking-widest">
                  Şu an çalıştığın program
                </p>
              )}
            </div>

            {/* Price display for paid programs */}
            {isPaidProgram && program.price && (
              <div className="flex items-baseline gap-2.5 pt-1">
                <span className="font-black text-2xl text-primary tracking-tight">
                  ₺{program.price.toLocaleString("tr-TR")}
                </span>
                {program.oldPrice && program.oldPrice > program.price && (
                  <span className="font-semibold text-muted-foreground/60 text-sm line-through">
                    ₺{program.oldPrice.toLocaleString("tr-TR")}
                  </span>
                )}
              </div>
            )}

            {program.description && (
              <p className="line-clamp-2 text-muted-foreground/80 text-sm leading-relaxed">
                {program.description}
              </p>
            )}

            <div className="flex items-center gap-4 font-bold text-muted-foreground/60 text-xs uppercase tracking-widest">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {program.durationWeeks} Hafta
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {program.daysPerWeek} Gün
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              asChild
              variant="ghost"
              className="h-11 min-w-[100px] flex-1 rounded-xl bg-background/50 hover:bg-background"
            >
              <Link
                to="/program/$programId"
                params={{ programId: program.programId }}
              >
                Görüntüle
              </Link>
            </Button>

            {isActive ? (
              <Button
                asChild
                className="h-11 min-w-[120px] flex-1 rounded-xl shadow-none"
              >
                <Link
                  to="/program/$programId"
                  params={{ programId: program.programId }}
                >
                  Devam Et
                </Link>
              </Button>
            ) : isPaidProgram ? (
              <>
                <Button
                  onClick={handlePurchase}
                  disabled={checkoutMutation.isPending}
                  className="h-11 min-w-[120px] flex-1 rounded-xl font-bold shadow-none"
                >
                  <ShoppingBag className="mr-1.5 h-4 w-4" />
                  {checkoutMutation.isPending
                    ? "Yönlendiriliyor..."
                    : "Satın Al"}
                </Button>
                {program.accessCode && (
                  <JoinWithCodeDialog>
                    <Button
                      variant="outline"
                      className="h-11 min-w-[120px] flex-1 rounded-xl shadow-none"
                    >
                      Kodla Başlat
                    </Button>
                  </JoinWithCodeDialog>
                )}
              </>
            ) : (
              <ProgramActivationWizard
                programName={program.name}
                isPending={assignMutation.isPending}
                onConfirm={handleAssign}
                hasActiveProgram={isActive}
              >
                <Button
                  disabled={assignMutation.isPending}
                  className="h-11 min-w-[120px] flex-1 rounded-xl shadow-none"
                >
                  {assignMutation.isPending ? "..." : "Başlat"}
                </Button>
              </ProgramActivationWizard>
            )}
          </div>
        </div>
      </div>

      {/* Iyzico Checkout Dialog */}
      <IyzicoCheckoutDialog
        open={isCheckoutOpen}
        onOpenChange={setIsCheckoutOpen}
        paymentPageUrl={checkoutUrl}
        title={`${program.name} Satın Al`}
      />
    </div>
  );
}

function ProgramListSkeleton() {
  return (
    <div className="grid gap-8 md:grid-cols-2">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-2xl border border-transparent bg-muted/10"
        >
          <div className="aspect-video w-full overflow-hidden">
            <Skeleton className="h-full w-full bg-muted/20" />
          </div>
          <div className="flex flex-1 flex-col p-6">
            <div className="mb-6 flex-1 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-8 w-3/4 bg-muted/20" />
                <Skeleton className="h-4 w-1/2 bg-muted/20" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full bg-muted/20" />
                <Skeleton className="h-4 w-5/6 bg-muted/20" />
              </div>
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-20 bg-muted/20" />
                <Skeleton className="h-4 w-20 bg-muted/20" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-11 flex-1 rounded-xl bg-muted/20" />
              <Skeleton className="h-11 flex-1 rounded-xl bg-muted/20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
